import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../axiosInstance";

const BASE_URL = `${process.env.REACT_APP_LISTOGRAPHY_API_PREFIX}`;

// fetch a single list item by sid
export const fetchTVShowListItem = createAsyncThunk(
  "tvshows/fetchListItem",
  async (sid) => {
    const response = await axios.get(
      `${BASE_URL}/api/lists/tvshow/items/${sid}`
    );
    return response.data;
  }
);

// fetch all list items by sid
export const fetchTVShowListItems = createAsyncThunk(
  "tvshows/fetchListItems",
  async ({ sid, actor, rating, genre, language, page = 0, size = 30 }) => {
    const params = { page, size, sort: "dateStarted,desc" };
    if (actor) {
      params.actor = actor;
    }
    if (rating) {
      params.rating = rating;
    }
    if (genre) {
      params.genre = genre;
    }
    if (language) {
      params.language = language;
    }
    const response = await axios.get(
      `${BASE_URL}/api/lists/tvshow/${sid}/items`,
      {
        params,
      }
    );
    return response.data;
  }
);

// create list item
export const createTVShowListItem = createAsyncThunk(
  "tvShows/createTVShowListItem",
  async (tvShowListItem) => {
    const response = await axios.post(
      `${BASE_URL}/api/lists/tvshow/items`,
      tvShowListItem
    );
    return response.data;
  }
);

// update list item
export const updateTVShowListItem = createAsyncThunk(
  "tvShows/updateTVShowListItem",
  async (tvShowListItemUpdate) => {
    const response = await axios.put(
      `${BASE_URL}/api/lists/tvshow/items`,
      tvShowListItemUpdate
    );
    return response.data;
  }
);

// delete list item
export const deleteTVShowListItem = createAsyncThunk(
  "tvshows/deleteListItem",
  async (sid) => {
    const response = await axios.delete(
      `${BASE_URL}/api/lists/tvshow/items/${sid}`
    );
    return response.data;
  }
);

const tvShowListItemsSlice = createSlice({
  name: "tvShowListItems",
  initialState: {
    content: [],
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
    isLoadingItem: false, // for single item
    isLoadingItems: false, // for page of items
    tvShowListItem: null,
    error: null,
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch single tv show list item
      .addCase(fetchTVShowListItem.pending, (state) => {
        state.isLoadingItem = true;
        state.error = null;
      })
      .addCase(fetchTVShowListItem.fulfilled, (state, action) => {
        state.tvShowListItem = action.payload;
        state.isLoadingItem = false;
      })
      .addCase(fetchTVShowListItem.rejected, (state, action) => {
        state.isLoadingItem = false;
        state.error = action.error.message;
      })
      // fetch all tv show list items for a specific list
      .addCase(fetchTVShowListItems.pending, (state) => {
        state.isLoadingItems = true;
        state.error = null;
      })
      .addCase(fetchTVShowListItems.fulfilled, (state, action) => {
        const { content, number, size, totalPages, totalElements } =
          action.payload;
        state.content = content;
        state.page = number;
        state.totalPages = totalPages;
        state.totalElements = totalElements;
        state.isLoadingItems = false;
        state.error = null;
      })
      .addCase(fetchTVShowListItems.rejected, (state, action) => {
        state.isLoadingItems = false;
        state.error = action.error.message;
      })
      // create tv show list item
      .addCase(createTVShowListItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTVShowListItem.fulfilled, (state, action) => {
        state.tvShowListItem = action.payload; // created tv show list item
        state.isLoading = false;
      })
      .addCase(createTVShowListItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // update tv show list item
      .addCase(updateTVShowListItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTVShowListItem.fulfilled, (state, action) => {
        state.tvShowListItem = action.payload; // updated tv show list item
        state.isLoading = false;
      })
      .addCase(updateTVShowListItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })
      // delete single tv show list item
      .addCase(deleteTVShowListItem.pending, (state) => {
        state.isLoadingItem = true;
        state.error = null;
      })
      .addCase(deleteTVShowListItem.fulfilled, (state, action) => {
        state.tvShowListItem = action.payload;
        state.isLoadingItem = false;
      })
      .addCase(deleteTVShowListItem.rejected, (state, action) => {
        state.isLoadingItem = false;
        state.error = action.error.message;
      });
  },
});

export const { setPage } = tvShowListItemsSlice.actions;

export default tvShowListItemsSlice.reducer;
